import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Search, Plus, Pencil, Trash2, Loader2, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const EMPTY = { name: "", email: "", phone: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | customer (edit)
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try { const r = await api.get("/customers", { params: q ? { q } : {} }); setCustomers(r.data); }
    catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query), 300);
    return () => clearTimeout(t);
  }, [query, load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (c) => { setForm({ name: c.name || "", email: c.email || "", phone: c.phone || "" }); setEditing(c); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      if (editing && editing.id) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Customer saved");
      setEditing(null);
      load(query);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This won't affect existing orders.`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(query); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Directory</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Everyone who's dropped off film. Returning customers can be attached to a new QR at the Scan Station without re-typing their details.</p>
        </div>
        <Button onClick={openNew} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="new-customer-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add customer
        </Button>
      </div>

      <div className="relative max-w-md mb-5">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or phone" className="pl-9" data-testid="customer-search-input" />
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : customers.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
          {query ? "No customers match your search." : "No customers yet. They're added automatically at intake, or add one manually."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="customers-list">
          {customers.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3" data-testid={`customer-row-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-semibold truncate">{c.name}</div>
                  {c.visit_count > 0 && (
                    <Badge variant="secondary" className="shrink-0 mono text-[10px] uppercase tracking-wider" data-testid={`visits-${c.id}`}>
                      {c.visit_count >= 2 ? `returning · ${c.visit_count} visits` : "1 visit"}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mono mt-0.5">
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                  {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-ruby" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading">{editing && editing.id ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Enter the customer's name, email, and phone number.</DialogDescription>
          <div className="space-y-3">
            <div>
              <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="customer-name-input" />
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="customer-email-input" />
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="customer-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="customer-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="customer-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
